// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// EXTRACTION PLAN -- required by the expert-legibility rule in
// `.claude/rules/anti-ai-slop.md`: this module was past the 400-line threshold.
//
// ALREADY EXTRACTED: the judgment receipts -> `./AskV2ReviewJudgmentReceipt.tsx`. The tag and
// source filter -> `./AskV2ReviewTagFilter.tsx`. Which section a finding sits
// in -> `reviewCommentGroup` in `../../utils/visibleReviewComments.ts`. The
// section heading, toggle and list -> `./AskV2ReviewCommentSection.tsx`. One
// card -> `./AskV2ReviewCommentCard.tsx`; card behaviour belongs there, not here.
// The moved-to-a-section snackbar -> `./useReviewCommentMoveNotice.ts`. The
// header row -> `./AskV2ReviewCommentsHeader.tsx`. Section and band open state
// and keeping the selected card in sight -> `./useReviewCommentSections.ts`.
// Memory and claim citations -> `./useReviewCommentCitations.ts`. To decide
// / All and Group by -> `./useReviewListView.ts`. The props ->
// `./askV2ReviewCommentsListProps.ts`. Bulk selection ->
// `./useReviewBulkSelection.ts` and `./AskV2ReviewBulkBar.tsx`.
import { CheckCircle } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  AskV2ReviewBulkBar,
  AskV2ReviewBulkCheckbox,
} from "./AskV2ReviewBulkBar";
import { AskV2ReviewCommentCard } from "./AskV2ReviewCommentCard";
import { reviewCommentPermissions } from "./AskV2ReviewCommentCardParts";
import { AskV2ReviewCommentDetail } from "./AskV2ReviewCommentDetail";
import {
  AskV2ReviewCommentDrawer,
  drawerSteps,
} from "./AskV2ReviewCommentDrawer";
import { AskV2ReviewCommentSection } from "./AskV2ReviewCommentSection";
import { AskV2ReviewCommentsHeader } from "./AskV2ReviewCommentsHeader";
import { type AskV2ReviewCommentsListProps } from "./askV2ReviewCommentsListProps";
import { reviewListEmptyState } from "./AskV2ReviewFilterMenuParts";
import { AskV2ReviewGroupSection } from "./AskV2ReviewGroupSection";
import { AskV2ReviewSeverityBands } from "./AskV2ReviewSeverityBands";
import { groupMark, isMinorFinding } from "./pinGroups";
import {
  defaultActiveReviewCommentTags,
  type CommentWithOptionalTags,
} from "./reviewCommentTags";
import { useReviewBulkSelection } from "./useReviewBulkSelection";
import { useReviewCommentCitations } from "./useReviewCommentCitations";
import {
  openPinGroup,
  useReviewCommentSections,
} from "./useReviewCommentSections";
import { riskCounts, useReviewListView } from "./useReviewListView";

import { reviewCommentGroup } from "../../utils/visibleReviewComments";

export function AskV2ReviewCommentsList({
  comments,
  companionsOf,
  commentNumbers: commentNumbersProp,
  contextFiles = [],
  reviewFiles = [],
  selectedCommentId: selectedProp = null,
  authorNameByUserId,
  currentUserId,
  currentUserName,
  activeTags = defaultActiveReviewCommentTags(),
  alwaysOn = true,
  showTagFilter = false,
  onToggleTag = () => undefined,
  onToggleAlwaysOn = () => undefined,
  allComments,
  sourceFilter,
  judgmentPending = false,
  onJudge,
  onEdit,
  onDelete,
  onSelect,
  onClear,
  afterUnresolved,
  pinGroups,
  headerControls,
  pinGrouping,
  severityBands = false,
  scrollContainerRef,
  statusTabs = false,
  drawer = false,
  drawerChange,
  onShowComparison,
  onExitComparison,
  changedOnly,
  showMinor,
  onResetFilter,
  statusFilter,
  onJudgeMany,
}: AskV2ReviewCommentsListProps) {
  const { t } = useTranslation("ui");
  const sectionIdBase = useId();
  // A companion has no card of its own: its lead's card is the one selected.
  const leadOfCompanion = useMemo(() => {
    const byCompanion = new Map<string, string>();
    companionsOf?.forEach((list, lead) =>
      list.forEach((c) => byCompanion.set(c._id, lead)),
    );
    return byCompanion;
  }, [companionsOf]);
  const selectedCommentId =
    selectedProp == null
      ? null
      : (leadOfCompanion.get(selectedProp) ?? selectedProp);
  // why: the groups are built before the list narrows (changed comments
  // only), so a group keeps only the members this list was handed.
  const listedIds = useMemo(
    () => new Set(comments.map((comment) => comment._id)),
    [comments],
  );
  const commentNumbers = useMemo(() => {
    if (commentNumbersProp) return commentNumbersProp;
    const numbers: Record<string, number> = {};
    let index = 0;
    for (const comment of comments) {
      if (comment.isDeleted) continue;
      index += 1;
      numbers[comment._id] = index;
    }
    return numbers;
  }, [commentNumbersProp, comments]);

  const { memoryCitationsByComment, claimCitationsByComment } =
    useReviewCommentCitations(comments);

  const authorOptions = {
    aiLabel: t("aiReviewer"),
    currentUserId,
    currentUserName,
    authorNameByUserId,
    unknownAuthorLabel: t("member"),
  };

  // why: a ref, so the bar can mark ids before the writes land and the
  // moved-toast effect reads them when they do.
  const quietMoves = useRef(new Set<string>());
  // why: stable, or the moved-toast effect re-runs on every render.
  const undoMove = useCallback(
    (commentId: string) => onJudge?.(commentId, null),
    [onJudge],
  );
  const listView = useReviewListView({
    comments,
    selectedCommentId,
    enabled: statusTabs,
    allComments,
    statusFilter,
  });
  const emptyState = reviewListEmptyState({
    activeTags,
    alwaysOn,
    hiddenSourceCount: sourceFilter?.hidden.size ?? 0,
    statusNarrowed: listView.statusNarrowed,
    changedOnly,
  });
  const {
    sections: allSections,
    sectionSizes,
    unresolvedCount,
    showSections: hasShownComments,
    isSectionOpen,
    toggleSection,
    bandsOn,
    minorBandOpen,
    toggleMinorBand,
    bulkPool,
  } = useReviewCommentSections({
    comments: listView.shown,
    allComments,
    selectedCommentId,
    severityBands,
    grouped: pinGroups != null,
    statusTabs,
    onUndoMove: statusTabs && onJudge ? undoMove : undefined,
    quietMoves,
    pinGroups,
  });
  // why: pin groups keep their sections, or the artwork's group pins would
  // face a flat list with no group to match them.
  const flat = listView.flat && pinGroups == null;
  const showSections = hasShownComments && !flat;
  // To decide emptied by the type filter, not by every comment having a status.
  const showEmptyState =
    comments.length === 0 ||
    (statusTabs && listView.shown.length === 0 && !listView.allDecided);
  const bulkOn = statusTabs && onJudgeMany != null;
  // why: only an open comment that offers Relevant / Not relevant and whose
  // card is on screen can be ticked, so Select all never reaches a decided
  // comment or one in a closed section or folded minor band.
  const unresolvedOpen = !showSections || isSectionOpen("unresolved");
  const minorHidden = showSections && bandsOn && !minorBandOpen;
  const selectableIds = useMemo(
    () =>
      bulkOn && unresolvedOpen
        ? bulkPool
            .filter(
              (comment) =>
                reviewCommentGroup(comment) === "unresolved" &&
                !(minorHidden && isMinorFinding(comment)) &&
                reviewCommentPermissions({ comment, onJudge: onJudgeMany })
                  .canJudge,
            )
            .map((comment) => comment._id)
        : [],
    [bulkOn, unresolvedOpen, minorHidden, bulkPool, onJudgeMany],
  );
  const selection = useReviewBulkSelection(selectableIds);
  const sections = allSections.filter((section) =>
    listView.showsSection(section.key),
  );
  const riskSummary = (items: readonly CommentWithOptionalTags[]) => {
    if (!statusTabs || items.length === 0) return undefined;
    return t("askV2.review.riskSummary", riskCounts(items));
  };
  // The finding whose header delete opened the scope prompt. The prompt
  // renders in that card's open body, so delete on a closed card opens it.
  const [rejectingCommentId, setRejectingCommentId] = useState<string | null>(
    null,
  );
  // why: artwork selection never hits the delete handler; clearing when the selection IS this finding would hide the prompt a closed-card delete just opened
  useEffect(() => {
    setRejectingCommentId((current) =>
      current != null &&
      current !== selectedCommentId &&
      leadOfCompanion.get(current) !== selectedCommentId
        ? null
        : current,
    );
  }, [leadOfCompanion, selectedCommentId]);

  // What the list card and the drawer's open comment both draw from.
  const shared = (comment: CommentWithOptionalTags) => ({
    comment,
    companions: companionsOf?.get(comment._id),
    rejectingId: rejectingCommentId,
    commentNumber: commentNumbers[comment._id] ?? 0,
    authorOptions,
    claimCitations: claimCitationsByComment.get(comment._id),
    memoryCitations: memoryCitationsByComment.get(comment._id),
    contextFiles,
    reviewFiles,
    authorNameByUserId,
    currentUserId,
    currentUserName,
    judgmentPending,
    onJudge,
    onEdit,
    onDelete,
    onRejectingChange: setRejectingCommentId,
  });
  const renderComment = (comment: CommentWithOptionalTags) => (
    <AskV2ReviewCommentCard
      key={comment._id}
      {...shared(comment)}
      expanded={selectedCommentId === comment._id}
      rejecting={rejectingCommentId === comment._id}
      onSelect={onSelect}
      onClear={onClear}
      rowActions={statusTabs}
      summary={drawer}
      leading={
        selectableIds.includes(comment._id) ? (
          <AskV2ReviewBulkCheckbox
            checked={selection.isSelected(comment._id)}
            label={t("askV2.review.bulkSelectComment", {
              number: commentNumbers[comment._id] ?? 0,
            })}
            data-testid={`ask-v2-review-bulk-select-${comment._id}`}
            onChange={(checked) => selection.toggle(comment._id, checked)}
          />
        ) : undefined
      }
    />
  );

  const steps = drawer
    ? drawerSteps({
        sections: flat
          ? [listView.shown]
          : sections.map((section) => section.items),
        flat,
        commentNumbers,
        selectedId: selectedCommentId,
      })
    : null;
  if (steps) {
    return (
      <AskV2ReviewCommentDrawer
        steps={steps}
        onStep={onSelect}
        onBack={onClear}
        tab={statusTabs ? listView.tab : undefined}
      >
        {/* why: keyed, so stepping to the next comment drops an unsaved edit */}
        <AskV2ReviewCommentDetail
          key={steps.comment._id}
          {...shared(steps.comment)}
          change={drawerChange}
          onShowComparison={onShowComparison}
          onExitComparison={onExitComparison}
        />
      </AskV2ReviewCommentDrawer>
    );
  }

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="ask-v2-review-comments"
      aria-label={t("askV2.review.findingsLabel")}
    >
      <AskV2ReviewCommentsHeader
        unresolvedCount={unresolvedCount}
        showSplit={hasShownComments}
        controls={headerControls}
        showTagFilter={showTagFilter}
        activeTags={activeTags}
        alwaysOn={alwaysOn}
        onToggleTag={onToggleTag}
        onToggleAlwaysOn={onToggleAlwaysOn}
        sourceFilter={sourceFilter}
        statusTabs={
          statusTabs
            ? {
                tab: listView.tab,
                counts: listView.counts,
                onChange: listView.setTab,
              }
            : undefined
        }
        groupBy={
          statusTabs
            ? { value: listView.groupBy, onChange: listView.setGroupBy }
            : undefined
        }
        pinGrouping={statusTabs ? pinGrouping : undefined}
        statusFilter={statusTabs ? listView.statusFilter : undefined}
        showMinor={statusTabs ? showMinor : undefined}
        onResetFilter={onResetFilter}
        selectAll={
          selectableIds.length > 0 ? (
            <AskV2ReviewBulkCheckbox
              checked={selection.allState}
              label={t("askV2.review.bulkSelectAll")}
              data-testid="ask-v2-review-bulk-select-all"
              onChange={selection.toggleAll}
            />
          ) : undefined
        }
      />
      <ul
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        data-testid="ask-v2-review-comment-list"
        aria-label={t("askV2.review.commentsLabel")}
        // why: a memory chip's card keeps inside this list, scrolling yet or not, so a short list never lets it slide over the artwork pane (`popoverBoundary`).
        data-popover-boundary
      >
        {showEmptyState ? (
          <li
            className="px-2 py-6 text-center text-[13px] text-puntt-neutral-gray-10"
            data-testid={emptyState.testId}
          >
            {t(emptyState.copyKey)}
          </li>
        ) : null}
        {listView.allDecided ? (
          <li
            className="flex flex-col items-center gap-2 px-2 py-8 text-center text-[13px] text-puntt-neutral-gray-11"
            data-testid="ask-v2-review-all-decided"
          >
            {/* why: Radix green, as the Confirm button uses; the tailwind green classes render gray in this panel. */}
            <Text as="span" color="green" className="flex">
              <CheckCircle size={28} weight="fill" aria-hidden="true" />
            </Text>
            {t("askV2.review.allDecided")}
          </li>
        ) : null}
        {showSections
          ? sections.map((section) => (
              <Fragment key={section.key}>
                <AskV2ReviewCommentSection
                  sectionKey={section.key}
                  label={t(section.labelKey)}
                  count={section.items.length}
                  riskSummary={riskSummary(section.items)}
                  hiddenCount={Math.max(
                    0,
                    sectionSizes[section.key] - section.items.length,
                  )}
                  open={isSectionOpen(section.key)}
                  listId={`${sectionIdBase}-${section.key}`}
                  onToggle={() => toggleSection(section.key)}
                >
                  {!isSectionOpen(section.key) ? null : section.key ===
                      "unresolved" && pinGroups ? (
                    pinGroups.groups.map((group, index) => {
                      const open = openPinGroup(group, listedIds);
                      if (!open) return null;
                      return (
                        <AskV2ReviewGroupSection
                          key={group.key}
                          group={open}
                          mark={groupMark(index)}
                          open={pinGroups.openGroupKey === group.key}
                          onToggle={() => pinGroups.onToggleGroup(group.key)}
                        >
                          {open.members.map(renderComment)}
                        </AskV2ReviewGroupSection>
                      );
                    })
                  ) : section.key === "unresolved" && bandsOn ? (
                    <AskV2ReviewSeverityBands
                      items={section.items}
                      minorOpen={minorBandOpen}
                      onToggleMinor={toggleMinorBand}
                      renderComment={renderComment}
                    />
                  ) : (
                    section.items.map(renderComment)
                  )}
                </AskV2ReviewCommentSection>
                {section.key === "unresolved" ? afterUnresolved : null}
              </Fragment>
            ))
          : listView.shown.map(renderComment)}
        {showSections ? null : afterUnresolved}
      </ul>
      {onJudgeMany ? (
        <AskV2ReviewBulkBar
          selection={selection}
          pending={judgmentPending}
          quietMoves={quietMoves}
          onJudge={onJudgeMany}
        />
      ) : null}
    </section>
  );
}
